import React, { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import {
  List,
  Typography,
  TextField,
} from '@worthy-npm/worthy-common-ui-components';
import {
  NextStepButton, Step, StepContainer, StepGridContainer,
} from '../../../styles/common';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { selectBrand, selectModel, updateModel } from '../../../slices/submitSlice';
import { FormContent, FormWrap, StepProps } from '../common';
import { parametrize } from '../../../lib/commonUtils';
import StepTitle from '../../stepTitle';
import GA from '../../../data/GA';
import { DEBOUNCE_DELAY, DEBOUNCE_OPTIONS } from '../../../data/constants';
import { ModelByBrand } from '../../../data/ItemDetails';
import { StyledButton, StyledList } from '../../common/ButtonsList';
import CTAButton from '../../common/ContinueButton';

function StepModel({
  next, active, prev, title, stepCaption, props,
}: StepProps) {
  const dispatch = useAppDispatch();
  const brand = useAppSelector(selectBrand);
  const model = useAppSelector(selectModel);
  const [customModel, setCustomModel] = useState(model);
  const step = 'model';

  // @ts-ignore
  const modelByBrand = ModelByBrand[brand] || [];

  const modelByBrandSorted = modelByBrand.sort((current: string, nextVal: string) => {
    if (current === 'Other') return 1;
    if (nextVal === 'Other') return -1;
    return current.localeCompare(nextVal);
  });

  useEffect(() => {
    if (!active) return;
    GA.stepView(step);
  }, [active]);

  const debouncedModelUpdate = useCallback(
    debounce((e: React.MouseEvent<HTMLElement>, item: string) => {
      dispatch(updateModel(item));
      GA.select({ step, value: item });
      next(e);
    }, DEBOUNCE_DELAY, DEBOUNCE_OPTIONS),
    [dispatch, step, next],
  );

  return (
    <StepContainer data-automation="model-material-step">
      <Step>
        <StepTitle
          prev={prev}
          stepCaption={stepCaption}
          stepTitle={title || 'What is your watch model?'}
          stepNotice={brand === 'other' ? 'Please enter model name' : 'Select an option below'}
          optionalProps={props || {}}
        />

        <StepGridContainer>
          <List spacing={2}>
            {brand === 'other' ? (
              <FormWrap>
                <FormContent
                  active
                  data-testid="model-input-form"
                  data-automation="model-input-form"
                  style={{ height: '64px' }}
                >
                  <TextField
                    fullWidth
                    style={{ padding: '2px 4px' }}
                    inputProps={{ maxLength: 25 }}
                    data-automation="model-input"
                    variant="standard"
                    value={customModel}
                    onChange={(e) => {
                      setCustomModel(e.target.value);
                    }}
                    sx={{ input: { textAlign: 'center' } }}
                  />
                </FormContent>
              </FormWrap>
            ) : (
              <StyledList>
                {modelByBrandSorted.map((item: string) => (
                  <StyledButton
                    selected={model === item}
                    data-automation={`model-material-step-item-${parametrize(item)}`}
                    key={item}
                    onClick={(e) => {
                      debouncedModelUpdate(e, item);
                    }}
                  >
                    <Typography textTransform="capitalize">{item}</Typography>
                  </StyledButton>
                ))}
              </StyledList>
            )}
          </List>

          <NextStepButton>
            {brand === 'other' ? (
              <CTAButton
                data-automation="model-next-button"
                onClick={(e) => {
                  debouncedModelUpdate(e, customModel);
                }}
                disabled={!customModel.length}
              />
            ) : null}
          </NextStepButton>
        </StepGridContainer>
      </Step>
    </StepContainer>
  );
}

export default StepModel;
